import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  clearCardFormToken,
  decodeTokenForCardForm,
  saveCardToClient
} from 'src/store/actions/cardFormActions';
import {
  initializeOpenPay,
  getFormFields,
  formatCardData,
  validateWithOpenPay,
  getDeviceSessionId,
  extractTokenFromResponse,
  extractErrorMessage,
  formatSyncErrorMessage
} from 'src/utils/openPayUtils';
import TokenValidationStatus from 'src/utils/constants/cardForm';

const MERCHANT_ID = process.env.REACT_APP_OPENPAY_MERCHANT_ID;
const PUBLIC_KEY = process.env.REACT_APP_OPENPAY_PUBLIC_KEY;

const useOpenPayCardForm = (token, tokenValidationStatus, clientId) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) {
      dispatch(decodeTokenForCardForm(token));
    }

    return () => {
      dispatch(clearCardFormToken());
    };
  }, [token, dispatch]);

  useEffect(() => {
    if (tokenValidationStatus !== TokenValidationStatus.SUCCESS) {
      return;
    }

    try {
      initializeOpenPay(
        MERCHANT_ID,
        PUBLIC_KEY,
        process.env.REACT_APP_NODE_ENV !== 'production'
      );
    } catch (err) {
      setError(t('OPENPAY_CARD_FORM.ERRORS.OPENPAY_NOT_AVAILABLE'));
    }
  }, [tokenValidationStatus, t]);

  const handleFormSubmit = async formValues => {
    setError(null);

    const { OpenPay } = window;

    if (!OpenPay) {
      setError(t('OPENPAY_CARD_FORM.ERRORS.OPENPAY_NOT_AVAILABLE'));
      return;
    }

    const fields = getFormFields();
    if (!fields) {
      setError(t('OPENPAY_CARD_FORM.ERRORS.FORM_NOT_AVAILABLE'));
      return;
    }

    if (fields.holderName) fields.holderName.value = formValues.holderName;
    if (fields.cardNumber) fields.cardNumber.value = formValues.cardNumber;
    if (fields.expirationMonth)
      fields.expirationMonth.value = formValues.expirationMonth;
    if (fields.expirationYear)
      fields.expirationYear.value = formValues.expirationYear;
    if (fields.cvv) fields.cvv.value = formValues.cvv;

    if (!OpenPay.getId || !OpenPay.getApiKey) {
      setLoading(false);
      setError(
        `${t(
          'OPENPAY_CARD_FORM.ERRORS.TOKEN_GENERATION_FAILED'
        )}: OpenPay no está inicializado correctamente`
      );
      return;
    }

    setLoading(true);

    const cardData = formatCardData(fields);
    const openPayValidationError = validateWithOpenPay(cardData, OpenPay);
    if (openPayValidationError) {
      setLoading(false);
      setError(openPayValidationError);
      return;
    }

    const successCallback = response => {
      const openpayTokenId = extractTokenFromResponse(response);

      setTimeout(async () => {
        try {
          if (!openpayTokenId) {
            setLoading(false);
            setError(t('OPENPAY_CARD_FORM.ERRORS.TOKEN_GENERATION_FAILED'));
            return;
          }

          let deviceSessionId;
          try {
            deviceSessionId = getDeviceSessionId();
          } catch (deviceError) {
            setLoading(false);
            setError(
              `${t('OPENPAY_CARD_FORM.ERRORS.DEVICE_SESSION_ERROR') ||
                'Error al obtener device session'}: ${deviceError.message ||
                'Error desconocido'}`
            );
            return;
          }

          if (!clientId) {
            setLoading(false);
            setError(
              t('OPENPAY_CARD_FORM.ERRORS.CLIENT_ID_REQUIRED') ||
                'Client ID is required'
            );
            return;
          }

          const cardDataToSave = {
            tokenId: openpayTokenId,
            clientId:
              typeof clientId === 'number' ? clientId : parseInt(clientId, 10),
            deviceSessionId
          };

          await dispatch(saveCardToClient(cardDataToSave));

          setLoading(false);
        } catch (err) {
          setLoading(false);
          setError(
            `${t(
              'OPENPAY_CARD_FORM.ERRORS.TOKEN_GENERATION_FAILED'
            )}: ${err.message || 'Error desconocido'}`
          );
        }
      }, 0);
    };

    const errorCallback = response => {
      setTimeout(() => {
        try {
          setLoading(false);
          const errorMessage = extractErrorMessage(response, t);
          setError(errorMessage);
        } catch (err) {
          setLoading(false);
          setError(t('OPENPAY_CARD_FORM.ERRORS.TOKEN_GENERATION_FAILED'));
        }
      }, 0);
    };

    try {
      OpenPay.token.create(cardData, successCallback, errorCallback);
    } catch (syncError) {
      setLoading(false);
      setError(formatSyncErrorMessage(syncError));
    }
  };

  return {
    loading,
    error,
    handleFormSubmit
  };
};

export default useOpenPayCardForm;
